// Explicit React import: consumers transform this file with their OWN jest/
// vite configs; apps/app's jest uses the classic JSX runtime.
import * as React from 'react';
import {
  BANNER_TEMPLATES,
  BANNER_FONTS,
  SPORT_COLOR_PALETTES,
  resolveTemplateKey,
  templateArtUrl,
} from '../banners';

/**
 * The single banner-template renderer, used by BOTH the public site
 * (LeagueTournamentLayout) and the admin BannerEditor thumbnails/preview —
 * same discipline as BlockRenderer: one implementation, plain JSX + plain CSS
 * (packages/shared/styles/bannerTemplates.css), no MUI/Tailwind, no
 * app-specific imports.
 *
 * Replaces the retired SVG `getBannerSvg` (2016-era flat templates rendered
 * via <img data:svg>, which could not load external images or real webfonts).
 * Backgrounds are the shared gpt-image-2 artwork (script 30); the palette
 * applies parametrically via tinted overlays + typography, so all 35
 * sport-palettes work against 14 static images. Legacy stored template keys
 * resolve through LEGACY_TEMPLATE_MAP (migrate-all, owner decision).
 */
export interface TemplateBannerProps {
  sport: string;
  templateKey?: string | null;
  paletteKey?: string | null;
  /** Direct colors override paletteKey when provided. */
  colors?: { primary: string; secondary: string } | null;
  fontKey?: string | null;
  orgName?: string | null;
  /** Org logo rendered INSIDE the banner (owner 2026-09-09) — vertically
   * centered at the chosen side; name/accent shift out of its way via the
   * staty-banner--logo-* modifier classes. */
  logoUrl?: string | null;
  logoPosition?: 'left' | 'right' | null;
  className?: string;
}

const FALLBACK_COLORS = { primary: '#F97316', secondary: '#18181B' };

export default function TemplateBanner({
  sport,
  templateKey,
  paletteKey,
  colors,
  fontKey,
  orgName,
  logoUrl,
  logoPosition,
  className,
}: TemplateBannerProps) {
  const key = resolveTemplateKey(templateKey);
  const template = BANNER_TEMPLATES[key];
  const palette =
    colors ||
    (SPORT_COLOR_PALETTES[sport] || SPORT_COLOR_PALETTES.basketball)?.[paletteKey || ''] ||
    FALLBACK_COLORS;
  const font = BANNER_FONTS[fontKey || ''] || BANNER_FONTS.bebas_neue;
  const name = (orgName || 'Your League').toUpperCase();
  const artUrl = template.artStyle ? templateArtUrl(sport, template.artStyle) : null;

  // Long names shrink instead of overflowing (the old SVG hard-coded 26px).
  const sizeClass = name.length > 26 ? 'staty-banner--long' : name.length > 16 ? 'staty-banner--mid' : '';
  // Absent position resolves to 'left' at render time (no stored default).
  const logoClass = logoUrl ? `staty-banner--logo-${logoPosition === 'right' ? 'right' : 'left'}` : '';

  const vars = {
    '--banner-primary': palette.primary,
    '--banner-secondary': palette.secondary,
    '--banner-font': font.family,
    '--banner-font-weight': String(font.weight),
  } as React.CSSProperties;

  return (
    <div
      className={`staty-banner staty-banner--${template.layout} ${sizeClass} ${logoClass} ${className || ''}`}
      style={vars}
    >
      {artUrl && (
        <div className="staty-banner__art" style={{ backgroundImage: `url(${artUrl})` }} />
      )}
      <div className="staty-banner__overlay" />
      {template.layout === 'split' && <div className="staty-banner__panel" />}
      {logoUrl && <img className="staty-banner__logo" src={logoUrl} alt="" />}
      {template.layout === 'glass' ? (
        <div className="staty-banner__card">
          <span className="staty-banner__name">{name}</span>
        </div>
      ) : (
        <span className="staty-banner__name">{name}</span>
      )}
      <div className="staty-banner__accent" />
    </div>
  );
}
